import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getIn } from 'formik'

import StoriesCarouselItem from './StoriesCarouselItem'
import Button from '../../../../../../../../../components/Button'
import ErrorMessage from '../../../../../../../../../components/Form/ErrorMessage'

import useLineItemDetails from '../../../../hooks/useLineItemDetails'

import {
  CLICK_THROUGH_LINK,
  initialStoriesItem,
  IS_ITEM_FORM_OPENED,
  STORIES_ITEM_SELECTED_INDEX,
  STORIES_ITEMS
} from '../../../../../../fields'

import useStyles from './styles'

const CarouselStories = ({ formik, showError, setShowError, isStepFieldsValid }) => {
  const classes = useStyles()

  const { t } = useTranslation()
  const { values, errors, setFieldValue, setValues } = formik

  const { isVideoViews } = useLineItemDetails()

  const storiesItemFormTitle = isVideoViews ? 'video' : 'image or video'

  const addAnotherButtonText = isVideoViews ? 'Add Another Video' : 'Add Another Image or Video'

  const addMoreStoriesDescription = isVideoViews
    ? 'Adding more videos will create a series of up to 3 stories.'
    : 'Adding more images or videos will create a series of up to 3 stories.'

  const storiesItems = values[STORIES_ITEMS]
  const selectedItemIndex = values[STORIES_ITEM_SELECTED_INDEX]
  const activeStoryPath = `${STORIES_ITEMS}[${selectedItemIndex}]`

  const numberOfStoriesError = useMemo(() => {
    return typeof errors[STORIES_ITEMS] === 'string' && errors[STORIES_ITEMS]
  }, [errors])

  const handleAddAnotherStory = useCallback(() => {
    // add new initial item to storiesItems array
    // CLICK_THROUGH_LINK from previous item must be added to new item
    const newStoryItem = {
      ...initialStoriesItem,
      [CLICK_THROUGH_LINK]: getIn(values, `${activeStoryPath}.${CLICK_THROUGH_LINK}`)
    }
    setValues({
      ...values,
      [STORIES_ITEMS]: [...storiesItems, newStoryItem],
      [STORIES_ITEM_SELECTED_INDEX]: storiesItems.length
    })
    setShowError(false)
  }, [storiesItems, activeStoryPath, values, setValues, setShowError])

  const handleDeleteItem = useCallback(
    index => {
      // delete item from carousel items array
      storiesItems.splice(index, 1)
      setFieldValue(STORIES_ITEMS, storiesItems)

      // select previous item if deleted item wasn't first
      if (index !== 0) {
        setFieldValue(STORIES_ITEM_SELECTED_INDEX, selectedItemIndex - 1)
      }
    },
    [storiesItems, setFieldValue, selectedItemIndex]
  )

  const handleItemClick = useCallback(
    ({ index, isFormOpened }) => {
      setFieldValue(STORIES_ITEM_SELECTED_INDEX, index)
      setFieldValue(IS_ITEM_FORM_OPENED, isFormOpened)
    },
    [setFieldValue]
  )

  return (
    <>
      {storiesItems.map((item, index) => (
        <StoriesCarouselItem
          key={index}
          formik={formik}
          index={index}
          showError={showError}
          onItemDelete={handleDeleteItem}
          onItemClick={handleItemClick}
          isStepFieldsValid={isStepFieldsValid}
          storiesItemFormTitle={storiesItemFormTitle}
        />
      ))}
      {storiesItems.length < 3 && (
        <Button type={'button'} className={'btnBlock'} onClick={handleAddAnotherStory}>
          {t(addAnotherButtonText)}
        </Button>
      )}
      {numberOfStoriesError && <ErrorMessage error={numberOfStoriesError} />}
      {storiesItems.length < 3 && <div className={classes.additionalInfo}>{t(addMoreStoriesDescription)}</div>}
    </>
  )
}

export default CarouselStories
